import {Client} from "../../Client.ts";
import {FormValues, Rule} from "./types.ts";
import type {RuleOperatorEnum} from '../../../../../generated/enums.ts';

function dateToIsoString(date: string | Date): string {
    if (date instanceof Date) return date.toISOString();
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return `${date}T00:00:00.000Z`;
    return String(date);
}

export function buildInitialBlacklistRuleState(
    clients: Client[],
    initialBusinessId: string | null,
    initialRelationName: string | null,
    initialRelationIban: string | null,
    initialAmount: number | null,
    initialDescription: string | null,
): { rules: Rule[]; selectedBusiness: Client | null } {
    const rules: Rule[] = [];
    let selectedBusiness: Client | null = null;

    if (initialBusinessId) {
        const business = clients.find((c) => c.id === initialBusinessId) || null;
        if (business) {
            selectedBusiness = business;
            rules.push({
                value_field_name: "business_id",
                operator: 'EQUALS',
                operand: initialBusinessId,
                context: null,
            });
        }
    }

    if (initialRelationName) {
        rules.push({
            value_field_name: "relation_name",
            operator: 'EQUALS',
            operand: initialRelationName,
            context: null,
        });
    }

    if (initialRelationIban) {
        rules.push({
            value_field_name: "relation_iban",
            operator: 'EQUALS',
            operand: initialRelationIban,
            context: null,
        });
    }

    if (initialAmount !== null && !Number.isNaN(initialAmount)) {
        rules.push({
            value_field_name: "amount",
            operator: 'EQUALS',
            operand: initialAmount,
            context: null,
        });
    }

    if (initialDescription) {
        rules.push({
            value_field_name: "desc",
            operator: 'EQUALS',
            operand: initialDescription,
            context: null,
        })
    }

    return {rules, selectedBusiness};
}

export function isOperandValid(operandValue: any): boolean {
    if (operandValue === null || operandValue === '' || operandValue === undefined) {
        return false;
    }
    if (typeof operandValue === 'number') {
        return !isNaN(operandValue);
    }
    return true;
}

export function getMultipleFieldsAsArray<T>(formData: any, fieldNames: string[]): Array<T> {
    const result: T[] = [];
    if (!formData || fieldNames.length === 0) return result;

    const rootField = fieldNames[0];
    const snakeToCamel = (str: string) => str.split('_').map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)).join('');

    const suffixes = new Set<string>();
    if (formData[rootField] !== undefined) suffixes.add("");

    Object.keys(formData).forEach(key => {
        if (key.startsWith(`${rootField}_`)) {
            const suffix = key.slice(rootField.length + 1);
            if (/^\d+$/.test(suffix)) suffixes.add(suffix);
        }
    });

    const sortedSuffixes = Array.from(suffixes).sort((a, b) => {
        if (a === "") return -1;
        if (b === "") return 1;
        return parseInt(a) - parseInt(b);
    });

    sortedSuffixes.forEach(suffix => {
        const item: any = {};
        let hasValue = false;
        fieldNames.forEach((fieldName, index) => {
            const key = index === 0
                ? (suffix ? `${fieldName}_${suffix}` : fieldName)
                : (suffix ? `${rootField}_${fieldName}_${suffix}` : `${rootField}_${fieldName}`);

            const value = formData[key];
            if (value !== undefined && value !== null && value !== '') {
                hasValue = true;
            }
            item[snakeToCamel(fieldName)] = value;
        });

        if (hasValue) {
            result.push(item as T);
        }
    });

    return result;
}

export function buildRule(formData: FormValues): Rule[] {
    const rules: Rule[] = [];

    if (formData.business?.id) {
        rules.push({
            value_field_name: "business_id",
            operator: 'EQUALS',
            operand: formData.business.id,
            context: null,
        });
    }

    if (formData.relations) {
        formData.relations.forEach(({name, operator}) => {
            if (name) {
                rules.push({
                    value_field_name: "relation_name",
                    operator: operator || 'EQUALS',
                    operand: name,
                    context: null,
                });
            }
        });
    } else {
        getMultipleFieldsAsArray<{ relationName: string, operator: RuleOperatorEnum }>(
            formData,
            ["relation_name", "operator"]
        ).map(({relationName, operator}) => {
            if (relationName) {
                rules.push({
                    value_field_name: "relation_name",
                    operator: operator || 'EQUALS',
                    operand: relationName,
                    context: null,
                });
            }
        });
    }

    if (formData.relation_iban) {
        rules.push({
            value_field_name: "relation_iban",
            operator: 'EQUALS',
            operand: formData.relation_iban,
            context: null,
        });
    }

    if (isOperandValid(formData.booking_date) || isOperandValid(formData.booking_date_upper)) {
        const operator = formData.booking_date_operator || 'EQUALS';
        if (operator === 'BETWEEN') {
            if (isOperandValid(formData.booking_date) !== isOperandValid(formData.booking_date_upper)) {
                throw new Error('Data: tussen range is niet geldig.');
            }
        }

        if (isOperandValid(formData.booking_date)) {
            rules.push({
                value_field_name: "booking_date",
                operator: operator,
                operand: dateToIsoString(formData.booking_date as string),
                context: operator === 'BETWEEN' && isOperandValid(formData.booking_date_upper)
                    ? {ceiling: dateToIsoString(formData.booking_date_upper as string)}
                    : null,
            });
        }
    }

    if (isOperandValid(formData.amount) || isOperandValid(formData.amount_upper)) {
        const operator = formData.amount_operator || 'EQUALS';
        if (operator === 'BETWEEN') {
            if (isOperandValid(formData.amount) !== isOperandValid(formData.amount_upper)) {
                throw new Error('Bedrag: tussen range is niet geldig.');
            }
        }

        if (isOperandValid(formData.amount)) {
            rules.push({
                value_field_name: "amount",
                operator: operator,
                operand: Number(formData.amount),
                context: operator === 'BETWEEN' && isOperandValid(formData.amount_upper)
                    ? {ceiling: Number(formData.amount_upper)}
                    : null,
            });
        }
    }

    if (formData.descriptions) {
        formData.descriptions.forEach(({text, operator}) => {
            if (text) {
                rules.push({
                    value_field_name: "desc",
                    operator: operator || 'EQUALS',
                    operand: text,
                    context: null,
                });
            }
        });
    } else if (formData.desc) {
        rules.push({
            value_field_name: "desc",
            operator: formData.desc_operator || 'EQUALS',
            operand: formData.desc,
            context: null,
        });
    }

    return rules;
}
